import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-black text-white py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-white hover:text-amber-500 transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-gray-400 text-lg">Last Updated: March 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">1. Introduction</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                LookWell Gents Salon ("we," "our," or "us") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information in compliance with the UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data and other applicable UAE laws.
              </p>
              <p className="text-gray-700 text-base leading-relaxed">
                By using our services or visiting our salon located in Dubai, United Arab Emirates, you consent to the data practices described in this policy.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-bold text-black mb-3">2.1 Personal Information</h3>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                We may collect the following personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base mb-4">
                <li>Name and contact details (phone number, email address)</li>
                <li>Appointment history and service preferences</li>
                <li>Payment and billing information</li>
                <li>Communication records with our salon</li>
                <li>Any other information you voluntarily provide</li>
              </ul>

              <h3 className="text-xl font-bold text-black mb-3">2.2 Automatically Collected Information</h3>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                When you visit our website, we may automatically collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li>IP address and browser information</li>
                <li>Device information and operating system</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website addresses</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                We use your personal information for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li>To provide and manage salon services and appointments</li>
                <li>To process payments and maintain transaction records</li>
                <li>To communicate with you about appointments, services, and promotions</li>
                <li>To improve our services and customer experience</li>
                <li>To comply with legal obligations under UAE law</li>
                <li>To prevent fraud and ensure security</li>
                <li>To send marketing communications (with your consent)</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">4. Data Sharing and Disclosure</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                We may share your information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base mb-4">
                <li><strong>Service Providers:</strong> Third-party vendors who assist in operations (payment processors, appointment systems)</li>
                <li><strong>Legal Authorities:</strong> When required by UAE law or legal process</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of our business</li>
              </ul>
              <p className="text-gray-700 text-base leading-relaxed">
                We do not sell your personal information to third parties.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">5. Data Security</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li>Secure data storage systems</li>
                <li>Encryption of sensitive information</li>
                <li>Regular security assessments</li>
                <li>Access controls and authentication procedures</li>
                <li>Staff training on data protection</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">6. Your Rights Under UAE Law</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                Under UAE Federal Decree-Law No. 45 of 2021, you have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li><strong>Right to Access:</strong> Request access to your personal data we hold</li>
                <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Right to Deletion:</strong> Request deletion of your personal data (subject to legal obligations)</li>
                <li><strong>Right to Object:</strong> Object to processing of your personal data for marketing purposes</li>
                <li><strong>Right to Data Portability:</strong> Request transfer of your data to another service provider</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
              </ul>
              <p className="text-gray-700 text-base leading-relaxed mt-4">
                To exercise these rights, please contact us using the details provided below.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">7. Data Retention</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by UAE law. Appointment records and transaction data are typically retained for a minimum period as required by UAE commercial and tax regulations.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">8. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                Our website uses cookies and similar tracking technologies to enhance your browsing experience. You can control cookie preferences through your browser settings. Please note that disabling cookies may affect website functionality.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">9. International Data Transfers</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                Your personal data is primarily stored and processed within the United Arab Emirates. If we transfer data outside the UAE, we ensure appropriate safeguards are in place to protect your information in accordance with UAE data protection laws.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">10. Children's Privacy</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                Our services are intended for individuals aged 18 and above. We do not knowingly collect personal information from minors without parental consent. If you believe we have collected information from a minor, please contact us immediately.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website with a revised "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">12. Contact Information</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 text-base mb-2"><strong>LookWell Gents Salon</strong></p>
                <p className="text-gray-700 text-base mb-2">Blue Building, Al Nahda 2</p>
                <p className="text-gray-700 text-base mb-2">Dubai, United Arab Emirates</p>
                <p className="text-gray-700 text-base mb-2">Phone: +971 564 740 785</p>
                <p className="text-gray-700 text-base">Email: inquiry@lookwellgentssalon.com</p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">13. Regulatory Authority</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                If you are not satisfied with our response to your privacy concerns, you have the right to lodge a complaint with the UAE Data Office or relevant regulatory authority in the UAE.
              </p>
            </div>

            <div className="bg-amber-50 p-6 rounded-lg mt-12">
              <p className="text-gray-700 text-base leading-relaxed">
                <strong>Consent:</strong> By using our services or providing your personal information, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;